import axios from 'axios';
import {
  Business,
  BusinessInvitation,
  BusinessMembership,
  LeaveBusinessResult,
} from '../types';
import { USE_MOCK_BUSINESS } from '../constants/buyer-portal';
import { businessMock } from '../mocks/businessMock';
import { clinicHttp, getClinicToken, getRefreshToken } from './http';
import { ENDPOINTS } from './endpoints';
import {
  BusinessDtoBackend,
  InvitationDtoBackend,
  mapBusinessDto,
  mapInvitationDto,
  mapMemberDto,
  MemberDtoBackend,
} from './businessMapper';

export type UpdateBusinessRequest = Partial<
  Pick<
    Business,
    | 'name'
    | 'abn'
    | 'industry'
    | 'businessPhone'
    | 'businessEmail'
    | 'contactPerson'
    | 'billingAddress'
    | 'shippingAddress'
  >
>;

export type CreateInvitationRequest = {
  email: string;
};

/** Phase 1 business creation only requires a name; other fields are set later. */
export type CreateBusinessRequest = {
  name: string;
};

export type BusinessService = {
  /** Returns the current user's Business or null when the user is Individual. */
  get: () => Promise<Business | null>;
  create: (req: CreateBusinessRequest) => Promise<Business>;
  update: (patch: UpdateBusinessRequest) => Promise<Business>;
  listMembers: () => Promise<BusinessMembership[]>;
  removeMember: (userId: string) => Promise<void>;
  listInvitations: () => Promise<BusinessInvitation[]>;
  createInvitation: (req: CreateInvitationRequest) => Promise<BusinessInvitation>;
  revokeInvitation: (invitationId: string) => Promise<void>;
  leave: () => Promise<LeaveBusinessResult>;
};

const realBusinessService: BusinessService = {
  get: async () => {
    try {
      const res = await clinicHttp.get<BusinessDtoBackend>(ENDPOINTS.BUSINESS.GET);
      return res.data ? mapBusinessDto(res.data) : null;
    } catch (err) {
      // Individual users (no active membership) get 404 from this endpoint.
      const status = (err as { status?: number })?.status;
      if (status === 404) return null;
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },
  create: async (req) => {
    const res = await clinicHttp.post<BusinessDtoBackend>(
      ENDPOINTS.BUSINESS.CREATE,
      req,
    );
    return mapBusinessDto(res.data);
  },
  update: async (patch) => {
    const res = await clinicHttp.put<BusinessDtoBackend>(
      ENDPOINTS.BUSINESS.UPDATE,
      patch,
    );
    return mapBusinessDto(res.data);
  },
  listMembers: async () => {
    const res = await clinicHttp.get<MemberDtoBackend[]>(ENDPOINTS.BUSINESS.MEMBERS);
    return (res.data ?? []).map(mapMemberDto);
  },
  removeMember: async (userId) => {
    await clinicHttp.post(ENDPOINTS.BUSINESS.REMOVE_MEMBER(userId));
  },
  listInvitations: async () => {
    const res = await clinicHttp.get<InvitationDtoBackend[]>(
      ENDPOINTS.BUSINESS.INVITATIONS,
    );
    return (res.data ?? []).map(mapInvitationDto);
  },
  createInvitation: async (req) => {
    const res = await clinicHttp.post<InvitationDtoBackend>(
      ENDPOINTS.BUSINESS.INVITATIONS,
      req,
    );
    return mapInvitationDto(res.data);
  },
  revokeInvitation: async (invitationId) => {
    await clinicHttp.post(ENDPOINTS.BUSINESS.REVOKE_INVITATION(invitationId));
  },
  leave: async () => {
    // Backend returns 200 with no body and doesn't rotate the JWT.
    // The store calls /auth/me after to refresh `currentBusiness`.
    await clinicHttp.post(ENDPOINTS.BUSINESS.LEAVE);
    return {
      leftBusinessId: '',
      newToken: {
        accessToken: getClinicToken() ?? '',
        refreshToken: getRefreshToken() ?? '',
        expiresIn: 0,
      },
    };
  },
};

export const businessService: BusinessService = USE_MOCK_BUSINESS
  ? businessMock
  : realBusinessService;
